// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { generateClient } from "aws-amplify/api";

import { PageContainer } from "../components/AssetBreadCrumb";
import AssetGraph from "../components/AssetGraphComponents/AssetGraph";
import { AssetSwitcherPanel, AssetMasthead, AssetMastheadSkeleton } from "../components/AssetPageParts";
import { Skeleton, SkeletonChart } from "../components/Skeleton";
import { PeekRail, PeekRailToggle, usePeekRail } from "../components/ReusableComponents/PeekRail";
import { useTour } from "../components/Tour/TourProvider";

import { getUser } from "../graphql/queries";
import { fetchAssetGroupById } from "../store/assetGroupSlice";
import { fetchAssetsByGroupId, assetSchema } from "../store/assetsSlice";
import { setModalState, modalViewsConst } from "../store/modalSlice";
import { schemaMatch } from "../util/utils";
import { getAssetTimeZoneContext } from "../util/timeZoneContext";

// The Asset page: masthead, then a control bar card, then the graph card with the group's other assets on a peek rail at its left edge.
const LEFT_RAIL_KEY = "erode.assetPage.switcherPinned";

const Dashboard = () => {
  let { groupid, assetid } = useParams();
  const dispatch = useDispatch();
  const [assetOwner, setAssetOwner] = useState(null);
  const assets = useSelector((state) => state.assets);
  const assetGroups = useSelector((state) => state.assetGroups);
  const user = useSelector((state) => state.user);
  const assetGroup = assetGroups.groups[groupid];
  const assetList = assets.dataGroups[groupid];

  const leftRail = usePeekRail(LEFT_RAIL_KEY);

  const fetchAssetAndGroup = async () => {
    if (!assetGroup) {
      await dispatch(fetchAssetGroupById(groupid));
    }
    if (!assetList) {
      await dispatch(fetchAssetsByGroupId(groupid));
    }
  };

  useEffect(() => {
    fetchAssetAndGroup();
  }, []);

  const selectedAsset = useMemo(() => {
    return assetList ? assetList.find((asset) => asset.id === assetid) : null;
  }, [assetList, assetid]);

  const activeRouteShort = useSelector(
    (state) => state.assets.activeProcessedModels[selectedAsset?.id]
  );

  // First visit to an Asset page: run the one-time blurred guide once the asset is loaded (so the anchors exist). Skipped if another tour is active or the flag is set. Stable pieces only — depending on the whole context re-fires this on every tour step.
  const { activeTour, hasFlag, start, flagsReady } = useTour();
  useEffect(() => {
    if (!selectedAsset || !flagsReady) return;
    if (activeTour || hasFlag("assetTourSeen")) return;
    // Skip the model-results steps up front when there's no processed model, so the tour doesn't blur-and-pause on absent targets at the end.
    const hasProcessedModel = (selectedAsset?.processedModels?.items?.length ?? 0) > 0;
    start("asset", { hasProcessedModel });
  }, [selectedAsset, flagsReady, activeTour, hasFlag, start]);

  const getAssetOwnerUser = async () => {
    // if the user is an admin, get the owner details - needed for S3 fetching
    if (selectedAsset && user?.permissionDetails?.id) {
      if (
        user?.permissionDetails?.id !== selectedAsset?.owner &&
        assetOwner?.id !== selectedAsset?.owner
      ) {
        try{
          // if (groups && groups.includes("Admin")) {
            const client = generateClient();
            const { data } = await client.graphql({
              query: getUser,
              variables: { id: selectedAsset?.owner },
            });

            if (data.getUser) {
              const { id, name, cognitoIdentityId, email, username, profilePictureUrl } = data.getUser;
              setAssetOwner({ id, name, cognitoIdentityId, email, username, profilePictureUrl });
            } else {
              console.log("User not found or access denied for ID:", selectedAsset?.owner);
              setAssetOwner(null);
            }
          }
          catch(e) {
            console.log("error", e)
          }
      }
    }
  };

  useEffect(() => {
    getAssetOwnerUser();
  }, [selectedAsset, user]);

  // The results modal inherits whatever time zone the graph is currently reading in, so downloaded runs line up with what's on screen.
  const handleOpenProcessedModels = () => {
    const { assetTimeZone, timeZone, timeZoneMode } =
      getAssetTimeZoneContext(selectedAsset?.id) || {};
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.processedModelDownload,
        properties: {
          __modalSize: "xl",
          asset: schemaMatch(selectedAsset, assetSchema),
          assetOwner: assetOwner ?? user.permissionDetails,
          initialRouteShort: activeRouteShort,
          inheritedTimeZone: timeZone,
          inheritedTimeZoneMode:
            timeZoneMode ||
            (timeZone === "UTC"
              ? "UTC"
              : assetTimeZone && timeZone === assetTimeZone
              ? "asset"
              : "local"),
          inheritedAssetTimeZone: assetTimeZone,
        },
      })
    );
  };

  // Placeholders in the real layout rather than a spinner, so the page lands in place
  if (!assetGroup || !assetList) {
    return (
      <PageContainer fullHeight>
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0">
            <AssetMastheadSkeleton />
          </div>
          {/* Mirrors the real bar pill for pill, so the row lands at the same 58/50px and nothing shifts */}
          <div className="eai-card mb-3 flex flex-none items-center gap-2 px-3 py-2">
            <Skeleton radius="full" className="h-8 w-8" />
            <Skeleton radius="full" className="hidden h-10 w-[13rem] md:block" />
            <Skeleton radius="full" className="h-8 w-32 md:w-36" />
            <div className="ml-auto flex items-center gap-2">
              <Skeleton radius="full" className="h-8 w-8 md:w-28" />
              <Skeleton radius="full" className="h-8 w-8 md:w-28" />
              <Skeleton radius="full" className="h-8 w-8 md:w-28" />
              <Skeleton radius="full" className="hidden h-8 w-28 md:block" />
            </div>
          </div>
          <div className="eai-card min-h-0 flex-1 overflow-hidden">
            <SkeletonChart label="Loading forecast" />
          </div>
        </div>
      </PageContainer>
    );
  }
  const organization = user.organizations?.data?.[assetGroup?.organizationId]
  const hasProcessedModels = (selectedAsset?.processedModels?.items?.length ?? 0) > 0;

  return (
    <PageContainer fullHeight>
      <div className="flex min-h-0 flex-1 flex-col">
        {/* The tour's overview step anchors here — the block carries the identity and the details, so the anchor is never behind a closed rail. */}
        <div data-tour="asset-description" className="shrink-0">
          <AssetMasthead
            asset={selectedAsset}
            assetGroup={assetGroup}
            organization={organization}
            hasProcessedModels={hasProcessedModels}
            onOpenResults={handleOpenProcessedModels}
          />
        </div>

        {/* AssetGraph owns the bar/graph split because the controls read its state; the graph takes the rest, so the page never scrolls */}
        <div data-tour="asset-graph" className="min-h-0 flex-1">
          <AssetGraph
            selectedAsset={selectedAsset}
            hideContainer
            headerBar
            graphLeadingPanel={
              /* Inside the graph card, so the peek covers the graph and leaves the bar that opened it — hence no reserved toggle lane */
              <PeekRail
                rail={leftRail}
                side="left"
                title="Assets"
                reserveToggleLane={false}
                testId="asset-switcher"
              >
                <AssetSwitcherPanel
                  assetList={assetList}
                  groupId={groupid}
                  activeAssetId={assetid}
                  onNavigate={leftRail.dismiss}
                />
              </PeekRail>
            }
            leadingControl={
              <PeekRailToggle
                rail={leftRail}
                side="left"
                standalone
                label={leftRail.pinned ? "Hide assets" : "Show assets"}
                data-tour="asset-list"
                data-testid="asset-switcher-toggle"
              />
            }
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default Dashboard;
